import clock from '@plugins/dayjs';

/**
 * Calendar date in Europe/London. Billing month-boundaries are London dates.
 * @param {string|null} [asOf] YYYY-MM-DD
 * @returns {string}
 */
function getEffectiveDate(asOf = null) {
    return asOf || clock().tz('Europe/London').format('YYYY-MM-DD');
}

/**
 * Normalise API date range payloads to { start, end }.
 * @param {object|null|undefined} dateRange
 * @returns {{ start: string|null, end: string|null }|null}
 */
export function normaliseDateRange(dateRange) {
    if (!dateRange || typeof dateRange !== 'object') {
        return null;
    }

    const start = dateRange.start ?? dateRange.starts_at ?? dateRange.from ?? null;
    const end = dateRange.end ?? dateRange.ends_at ?? dateRange.to ?? null;

    if (!start && !end) {
        return null;
    }

    return { start, end };
}

/**
 * Resolve the subscription that is effective on a given date.
 * Assumes non-overlapping date ranges per subscription type.
 * @param {Array<object>} subscriptions
 * @param {object} [options]
 * @param {string|null} [options.subscriptionType]
 * @param {string|null} [options.asOf] YYYY-MM-DD
 * @returns {object|null}
 */
export function getCurrentSubscription(subscriptions = [], { subscriptionType = null, asOf = null } = {}) {
    if (!Array.isArray(subscriptions) || !subscriptions.length) {
        return null;
    }

    const effectiveDate = getEffectiveDate(asOf);

    const matches = subscriptions.filter(subscription => {
        if (!subscription || typeof subscription !== 'object') {
            return false;
        }

        if (subscriptionType && subscription.subscription_type !== subscriptionType) {
            return false;
        }

        const range = normaliseDateRange(subscription.date_range);

        if (!range?.start) {
            return false;
        }

        if (range.start > effectiveDate) {
            return false;
        }

        if (range.end && range.end < effectiveDate) {
            return false;
        }

        return true;
    });

    if (!matches.length) {
        return null;
    }

    if (matches.length === 1) {
        return matches[0];
    }

    return [...matches].sort((left, right) => {
        const leftStart = normaliseDateRange(left.date_range)?.start || '';
        const rightStart = normaliseDateRange(right.date_range)?.start || '';

        return rightStart.localeCompare(leftStart);
    })[0];
}

/**
 * Resolve the next subscription that starts after a given date.
 * @param {Array<object>} subscriptions
 * @param {object} [options]
 * @param {string|null} [options.subscriptionType]
 * @param {string|null} [options.asOf] YYYY-MM-DD
 * @returns {object|null}
 */
export function getUpcomingSubscription(subscriptions = [], { subscriptionType = null, asOf = null } = {}) {
    if (!Array.isArray(subscriptions) || !subscriptions.length) {
        return null;
    }

    const effectiveDate = getEffectiveDate(asOf);

    const matches = subscriptions.filter(subscription => {
        if (!subscription || typeof subscription !== 'object') {
            return false;
        }

        if (subscriptionType && subscription.subscription_type !== subscriptionType) {
            return false;
        }

        const range = normaliseDateRange(subscription.date_range);

        if (!range?.start) {
            return false;
        }

        return range.start > effectiveDate;
    });

    if (!matches.length) {
        return null;
    }

    if (matches.length === 1) {
        return matches[0];
    }

    return [...matches].sort((left, right) => {
        const leftStart = normaliseDateRange(left.date_range)?.start || '';
        const rightStart = normaliseDateRange(right.date_range)?.start || '';

        return leftStart.localeCompare(rightStart);
    })[0];
}

/**
 * Whether a subscription is fully in the past (ended before the given date).
 * Current (open-ended or still covering today) and scheduled (future start) items are excluded.
 * @param {object|null|undefined} subscription
 * @param {object} [options]
 * @param {string|null} [options.asOf] YYYY-MM-DD
 * @returns {boolean}
 */
export function isHistoricalSubscription(subscription, { asOf = null } = {}) {
    if (!subscription || typeof subscription !== 'object') {
        return false;
    }

    const range = normaliseDateRange(subscription.date_range);

    if (!range?.start || !range.end) {
        return false;
    }

    return range.end < getEffectiveDate(asOf);
}

export default getCurrentSubscription;
